import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ensureUrl } from "@/lib/url";
import { toast } from "sonner";

/**
 * useDiscoverNearby — Google-Places-driven vendor discovery. Owns the
 * dialog state (query, location text, coords, loading, results, and
 * per-place import spinners), plus the geolocation, search, and import
 * handlers. The Vendors page just wires these into JSX.
 *
 * `onImported` — called after each successful import so the caller can
 * refresh its list.
 */
export default function useDiscoverNearby({ onImported } = {}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [locText, setLocText] = useState("");
  const [coords, setCoords] = useState(null); // { lat, lng }
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [importing, setImporting] = useState({});

  const requestGeo = useCallback(() => {
    if (!navigator.geolocation) {
      return toast.error("Geolocation not supported on this device");
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocText("");
        toast.success("Using your current location");
      },
      () => toast.error("Couldn't get your location — type a suburb, city or postcode instead"),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 },
    );
  }, []);

  const run = useCallback(async () => {
    if (!query.trim()) {
      return toast.error("What are you looking for? e.g. 'hardware store'");
    }
    if (!coords && !locText.trim()) {
      return toast.error("Share your location or type a suburb / city / postcode");
    }
    setLoading(true);
    try {
      const payload = { query: query.trim() };
      if (coords) { payload.lat = coords.lat; payload.lng = coords.lng; }
      else { payload.location_text = locText.trim(); }
      const { data } = await api.post("/vendors/discover", payload);
      setResults(data.results || []);
      if ((data.results || []).length === 0) {
        toast.info("No matches — try a broader search");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [query, coords, locText]);

  const importOne = useCallback(async (r) => {
    setImporting((m) => ({ ...m, [r.place_id]: true }));
    try {
      const { data } = await api.post("/vendors/import", {
        place_id: r.place_id,
        name: r.name,
        type: r.suggested_type,
        address: r.address,
        phone: r.phone,
        website: ensureUrl(r.website),
        rating: r.rating,
        lat: r.lat,
        lng: r.lng,
        hours: r.hours,
      });
      if (data.duplicate) toast.info(`${r.name} is already in your vendors`);
      else toast.success(`Added ${r.name}`);
      if (onImported) onImported();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting((m) => ({ ...m, [r.place_id]: false }));
    }
  }, [onImported]);

  const reset = useCallback(() => {
    setResults([]);
    setImporting({});
  }, []);

  return {
    // dialog
    open, setOpen,
    // inputs
    query, setQuery,
    locText, setLocText,
    coords, setCoords,
    // async
    loading, results, importing,
    // handlers
    requestGeo, run, importOne, reset,
  };
}
